import React from 'react'
import { Link } from 'react-router-dom'
import Grid from '../../components/components/Grid/Grid'
import { fomratWithCommasAndDecimals } from '../../shared/util/currencyUtils'
import { Labels } from './labels'
import { ICommissionReconciliationRow } from './models/CommissionReconciliationTypes'

/** Column config for the shared Grid. Sorting, sticky header and right-align
 *  come from Grid itself, only the column tints are ours. */
interface Props {
  rows: ICommissionReconciliationRow[]
}

// The outer <div> is required - .right-align targets a child div, so a bare
// <span> renders left-aligned. null money values show a dash.
const dashCell = (): React.ReactNode => (
  <div>
    <span>—</span>
  </div>
)

const moneyWithDollar = (value: number | null): React.ReactNode => {
  if (value === null || value === undefined) return dashCell()
  const isNeg = value < 0
  const formatted = fomratWithCommasAndDecimals(Math.abs(value))
  const display = `${isNeg ? '- ' : ''}$${formatted}`
  return (
    <div>
      <span className={isNeg ? 'text-danger fw-bold' : undefined}>{display}</span>
    </div>
  )
}

const moneyPlain = (value: number | null): React.ReactNode => {
  if (value === null || value === undefined) return dashCell()
  const isNeg = value < 0
  const formatted = fomratWithCommasAndDecimals(Math.abs(value))
  const display = `${isNeg ? '- ' : ''}${formatted}`
  return (
    <div>
      <span className={isNeg ? 'text-danger fw-bold' : undefined}>{display}</span>
    </div>
  )
}

const linkCell = (value: string): React.ReactNode => (
  <Link
    to="#"
    title={value}
    className="gridNavigateButton btn btn-link text-start text-decoration-none d-block px-0"
  >
    {value}
  </Link>
)

// Ellipsis plus tooltip if a name ever overflows, same as Customer Results.
const textOrDash = (value: string | null): React.ReactNode => (
  <div className="truncateText">
    <span title={value ?? ''}>{value ?? '—'}</span>
  </div>
)

// Fixed pixel widths - the shared Grid has no auto-fill, same as other CS2 grids.
const columns = [
  {
    Header: Labels.COL_ACCOUNT,
    accessor: 'accountNumber',
    Cell: ({ value }: { value: string }) => linkCell(value),
    width: 100,
  },
  {
    // Link cells truncate at the column width, and 110 clipped a full policy
    // number. Sized to match the Policy Number column on Customer Results.
    Header: Labels.COL_POLICY,
    accessor: 'policyNumber',
    Cell: ({ value }: { value: string }) => linkCell(value),
    width: 200,
    minWidth: 150,
  },
  {
    // Sized to the longest carrier and agency names in the reconciliation data.
    Header: Labels.COL_CARRIER,
    accessor: 'carrier',
    Cell: ({ value }: { value: string }) => textOrDash(value),
    width: 300,
    minWidth: 150,
  },
  {
    // Values here are short codes like HO3, so the header sets the width.
    Header: Labels.COL_POLICY_TYPE,
    accessor: 'policyType',
    Cell: ({ value }: { value: string | null }) => textOrDash(value),
    width: 120,
  },
  {
    Header: Labels.COL_TERM,
    accessor: 'term',
    width: 220,
  },
  {
    Header: Labels.COL_POLICY_PREMIUM,
    accessor: 'policyPremium',
    headerClassName: 'right-align-header',
    cellClassName: 'right-align',
    Cell: ({ value }: { value: number | null }) => moneyWithDollar(value),
    width: 140,
  },
  {
    Header: Labels.COL_ESTIMATED_COMMISSION,
    accessor: 'estimatedCommission',
    headerClassName: 'right-align-header',
    cellClassName: 'right-align',
    Cell: ({ value }: { value: number | null }) => moneyPlain(value),
    width: 140,
  },
  {
    Header: Labels.COL_IMPORTED_PREMIUM,
    accessor: 'importedPremium',
    headerClassName: 'right-align-header',
    cellClassName: 'right-align',
    Cell: ({ value }: { value: number | null }) => moneyPlain(value),
    width: 140,
  },
  {
    Header: Labels.COL_IMPORTED_COMMISSION,
    accessor: 'importedCommission',
    headerClassName: 'right-align-header commission-reconciliation-col-imported-commission',
    cellClassName: 'right-align commission-reconciliation-col-imported-commission',
    Cell: ({ value }: { value: number | null }) => moneyPlain(value),
    width: 140,
  },
  {
    // text-success is lightgreen in the CS2 theme and unreadable here.
    // text-success-toast is the green the policy status badge uses.
    Header: Labels.COL_RECONCILED,
    accessor: 'reconciled',
    Cell: ({ value }: { value: 'Yes' | 'No' }) => (
      <span className={value === 'Yes' ? 'text-success-toast fw-bold' : 'text-danger fw-bold'}>
        {value}
      </span>
    ),
    width: 100,
  },
  {
    Header: Labels.COL_VARIANCE,
    accessor: 'variance',
    headerClassName: 'right-align-header',
    cellClassName: 'right-align',
    Cell: ({ value }: { value: number | null }) => moneyPlain(value),
    width: 140,
  },
  {
    Header: Labels.COL_COMPENSATION,
    accessor: 'compensation',
    headerClassName: 'right-align-header commission-reconciliation-col-compensation',
    cellClassName: 'right-align commission-reconciliation-col-compensation',
    Cell: ({ value }: { value: number | null }) => moneyPlain(value),
    width: 140,
  },
  {
    Header: Labels.COL_AGENCY,
    accessor: 'agency',
    Cell: ({ value }: { value: string | null }) => textOrDash(value),
    width: 350,
    minWidth: 150,
  },
]

const CommissionReconciliationGrid: React.FunctionComponent<Props> = ({ rows }) => {
  if (!rows.length) {
    return (
      <div className="text-center text-muted p-5">{Labels.NO_DATA}</div>
    )
  }
  // Same Grid props as Customer Results, including the default page size of 10.
  // Container height is set in CommissionReconciliation.scss.
  return (
    <Grid
      id="commission-reconciliation-grid"
      data={rows}
      columns={columns}
      hiddenColumns={['selection']}
      sortDefault={[]}
      showFilters={false}
      grdTabelClass="table-striped table-sm"
      grdTableContainerClass="p-0 commission-reconciliation-grid-container"
    />
  )
}

export default CommissionReconciliationGrid
